import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, ChevronDown, Users, Calendar, Package, 
  UserCog, MessageSquare, MapPin, Trophy, Dumbbell,
  DollarSign, ClipboardCheck, Shield, History, Target,
  Shirt, Baby, Briefcase, Bell, Search, BarChart3,
  TrendingUp, FileText, AlertTriangle, Award, Boxes,
  Warehouse, PieChart, UserCheck, Eye, Lock, Layers,
  CheckCircle, Cpu, Wifi, Plug, MessageCircle, ZoomIn, X
} from 'lucide-react';

// ============================================
// SCROLL-DRIVEN HERO SECTION
// Logo grows until it fills the screen white
// ============================================

const HeroSection = () => {
  const sectionRef = useRef(null);
  const [scrollProgress, setScrollProgress] = useState(0);
  
  const handleScroll = useCallback(() => {
    if (!sectionRef.current) return;
    
    const rect = sectionRef.current.getBoundingClientRect();
    const sectionHeight = sectionRef.current.offsetHeight;
    
    const progress = Math.max(0, Math.min(1, -rect.top / (sectionHeight - window.innerHeight)));
    setScrollProgress(progress);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const cubicEase = scrollProgress * scrollProgress * scrollProgress;
  const logoScale = 1 + (cubicEase * 60);
  
  const textOpacity = Math.max(0, 1 - (scrollProgress * 8));
  const textTranslateY = scrollProgress * -100;
  
  const bgOpacity = scrollProgress < 0.5 
    ? 0.5 + (scrollProgress * 0.3)
    : 0.8 - ((scrollProgress - 0.5) * 1.6);
  
  const whiteOverlay = Math.max(0, (scrollProgress - 0.4) * 1.67);
  const bgImageOpacity = Math.max(0, 1 - (scrollProgress * 2));

  return (
    <section 
      ref={sectionRef}
      className="relative h-[400vh] bg-black"
    >
      <div className="sticky top-0 h-screen overflow-hidden bg-black">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center transition-opacity duration-100"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1508098682722-e99c43a406b2?q=80&w=2070&auto=format&fit=crop')`,
            opacity: bgImageOpacity,
          }}
        />
        
        {/* Dark overlay - always present initially */}
        <div 
          className="absolute inset-0 bg-black transition-opacity duration-100"
          style={{ opacity: bgOpacity }}
        />
        
        {/* White overlay - appears as logo grows */}
        <div 
          className="absolute inset-0 bg-white transition-opacity duration-100 pointer-events-none"
          style={{ opacity: whiteOverlay }}
        />

        <div className="relative h-full flex flex-col justify-center items-center pt-0">
          <div
            className="relative z-50 transition-transform duration-75 ease-out will-change-transform"
            style={{ transform: `scale(${logoScale})` }}
          >
            <img 
              src={`${process.env.PUBLIC_URL}/logos/LogoSC_Vertical_Blanco_Fondos_oscuros.svg`} 
              alt="Sport Control" 
              className="h-32 sm:h-40 md:h-48 lg:h-56 w-auto"
            />
          </div>
          
          <div
            className="text-center transition-all duration-75 ease-out px-4 mt-6 sm:mt-8"
            style={{
              transform: `translateY(${textTranslateY}px)`,
              opacity: textOpacity,
              pointerEvents: textOpacity < 0.5 ? 'none' : 'auto',
            }}
          >
            <p className="font-poppins text-base sm:text-lg md:text-xl lg:text-2xl text-neutral-300 max-w-2xl font-medium">
              La plataforma que <span className="text-[#6FB035] font-bold">unifica</span> toda la gestión de clubes de fútbol
            </p>
          </div>

          {/* Animated scroll indicator with line */}
          <button 
            onClick={() => {
              const targetScroll = window.innerHeight * 4.8;
              window.scrollTo({ top: targetScroll, behavior: 'smooth' });
            }}
            className="group mt-10 flex flex-col items-center gap-3 cursor-pointer transition-all duration-300"
            style={{
              opacity: textOpacity,
            }}
          >
            <span className="text-white/60 text-xs uppercase tracking-[0.2em] font-medium group-hover:text-[#6FB035] transition-colors duration-300">
              Comenzá a explorar
            </span>
            
            {/* Mouse icon with scroll wheel animation */}
            <div className="relative flex flex-col items-center">
              {/* Mouse outline */}
              <div className="relative w-6 h-10 rounded-full border-2 border-white/50 group-hover:border-[#6FB035] transition-colors duration-300 flex justify-center">
                {/* Animated scroll wheel */}
                <div className="w-1 h-2 bg-white/70 rounded-full mt-2 animate-scroll-wheel group-hover:bg-[#6FB035]" />
              </div>
              
              {/* Animated line below mouse */}
              <div className="relative h-12 w-[2px] mt-2 overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-b from-white/50 via-white/30 to-transparent animate-scroll-line" />
                <div className="absolute top-0 left-0 w-full bg-[#6FB035] animate-scroll-dot" style={{ height: '8px', borderRadius: '50%' }} />
              </div>
              
              {/* Triple chevron animation */}
              <div className="flex flex-col items-center -mt-1 gap-0">
                <ChevronDown className="w-4 h-4 text-white/40 animate-chevron-1 group-hover:text-[#6FB035]/60" />
                <ChevronDown className="w-4 h-4 text-white/60 -mt-2 animate-chevron-2 group-hover:text-[#6FB035]/80" />
                <ChevronDown className="w-4 h-4 text-white/80 -mt-2 animate-chevron-3 group-hover:text-[#6FB035]" />
              </div>
            </div>
          </button>
          
          {/* Custom animations */}
          <style>{`
            @keyframes scroll-wheel {
              0%, 100% { transform: translateY(0); opacity: 1; }
              50% { transform: translateY(4px); opacity: 0.3; }
            }
            @keyframes scroll-line {
              0% { transform: translateY(-100%); }
              100% { transform: translateY(100%); }
            }
            @keyframes scroll-dot {
              0% { transform: translateY(-8px); opacity: 0; }
              20% { opacity: 1; }
              80% { opacity: 1; }
              100% { transform: translateY(48px); opacity: 0; }
            }
            @keyframes chevron-1 {
              0%, 100% { opacity: 0.3; transform: translateY(0); }
              50% { opacity: 0.6; transform: translateY(3px); }
            }
            @keyframes chevron-2 {
              0%, 100% { opacity: 0.5; transform: translateY(0); }
              50% { opacity: 0.8; transform: translateY(3px); }
            }
            @keyframes chevron-3 {
              0%, 100% { opacity: 0.7; transform: translateY(0); }
              50% { opacity: 1; transform: translateY(3px); }
            }
            .animate-scroll-wheel {
              animation: scroll-wheel 1.5s ease-in-out infinite;
            }
            .animate-scroll-line {
              animation: scroll-line 2s ease-in-out infinite;
            }
            .animate-scroll-dot {
              animation: scroll-dot 2s ease-in-out infinite;
            }
            .animate-chevron-1 {
              animation: chevron-1 1.5s ease-in-out infinite;
            }
            .animate-chevron-2 {
              animation: chevron-2 1.5s ease-in-out infinite 0.15s;
            }
            .animate-chevron-3 {
              animation: chevron-3 1.5s ease-in-out infinite 0.3s;
            }
          `}</style>
        </div>

        {/* Hidden old scroll indicator */}
        <div 
          className="hidden"
        >
          <span className="text-white/70 text-sm uppercase tracking-[0.3em] font-semibold">Scroll</span>
          <div className="relative">
            {/* Pulsing ring effect */}
            <div className="absolute inset-0 -m-2 rounded-full bg-white/10 animate-ping" style={{ animationDuration: '2s' }} />
            <div className="relative w-10 h-10 rounded-full border-2 border-white/50 flex items-center justify-center">
              <ChevronDown className="w-6 h-6 text-white/80 animate-bounce" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// ============================================
// INTRO SECTION - "Todo lo que necesitás"
// Now mentions modular contracting
// ============================================

const IntroSection = () => {
  const sectionRef = useRef(null);
  const [scrollProgress, setScrollProgress] = useState(0);

  const handleScroll = useCallback(() => {
    if (!sectionRef.current) return;
    const rect = sectionRef.current.getBoundingClientRect();
    const sectionHeight = sectionRef.current.offsetHeight;
    const progress = Math.max(0, Math.min(1, -rect.top / (sectionHeight - window.innerHeight)));
    setScrollProgress(progress);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const titleOpacity = Math.min(1, scrollProgress * 4);
  const titleScale = 0.8 + (Math.min(scrollProgress * 3, 1) * 0.2);
  const subtitleOpacity = Math.max(0, Math.min(1, (scrollProgress - 0.2) * 3));
  const modulesOpacity = Math.max(0, Math.min(1, (scrollProgress - 0.4) * 3));
  
  return (
    <section 
      id="modulos"
      ref={sectionRef}
      className="relative h-[250vh] bg-white"
    >
      <div className="sticky top-0 h-screen flex flex-col items-center justify-between px-4 py-16">
        {/* Top spacer for balance */}
        <div className="flex-shrink-0" />
        
        {/* Main content - centered */}
        <div 
          className="text-center transition-all duration-100"
          style={{ 
            opacity: titleOpacity,
            transform: `scale(${titleScale})`
          }}
        >
          <h2 className="font-poppins font-black text-4xl sm:text-5xl md:text-6xl lg:text-7xl uppercase tracking-tight">
            <span className="text-[#0E82B5]">Todo</span>{' '}
            <span className="text-neutral-800">lo que</span>
            <br />
            <span className="text-neutral-800">necesitás</span>
          </h2>
          
          <div 
            className="mt-6 transition-all duration-300"
            style={{ opacity: subtitleOpacity }}
          >
            <p className="font-poppins text-base sm:text-lg md:text-xl text-neutral-500 max-w-3xl mx-auto leading-relaxed">
              La plataforma que organiza y gestiona desde captación hasta primera.
              <br className="hidden sm:block" />
              <span className="text-neutral-700 font-medium">Varios módulos integrados para una gestión deportiva y administrativa completa.</span>
            </p>
          </div>
        </div>
        
        {/* Bottom section - badges and scroll indicator pushed down */}
        <div className="flex-shrink-0 flex flex-col items-center gap-8 pb-4">
          {/* Modular contracting badges */}
          <div 
            className="flex flex-col sm:flex-row items-center justify-center gap-3 transition-all duration-500"
            style={{ 
              opacity: modulesOpacity,
              transform: `translateY(${(1 - modulesOpacity) * 20}px)`
            }}
          >
            {/* Main badge - dark background for emphasis */}
            <div className="flex items-center gap-3 px-5 py-3 bg-neutral-900 rounded-lg shadow-lg">
              <div className="w-8 h-8 rounded-md bg-[#0E82B5]/20 flex items-center justify-center">
                <AlertTriangle className="w-4 h-4 text-[#0E82B5]" />
              </div>
              <span className="font-semibold text-white">
                Contratá <span className="text-[#6FB035] font-bold">solo lo que necesites</span>
              </span>
            </div>
            
            {/* Secondary badge */}
            <div className="flex items-center gap-2 px-4 py-3 bg-neutral-100 rounded-lg border border-neutral-200">
              <div className="w-6 h-6 rounded-md bg-[#6FB035]/15 flex items-center justify-center">
                <CheckCircle className="w-4 h-4 text-[#6FB035]" />
              </div>
              <span className="text-sm text-neutral-600">
                Posibilidad de módulos <span className="text-[#0E82B5] font-semibold">a medida</span>
              </span>
            </div>
          </div>
          
          {/* Color dots */}
          <div className="flex justify-center gap-2">
            {['#0E82B5', '#6FB035', '#F59E0B', '#8B5CF6', '#06B6D4'].map((color, i) => (
              <div 
                key={i}
                className="w-3 h-3 rounded-full transition-all duration-500"
                style={{ 
                  backgroundColor: color,
                  transform: `scale(${Math.min(1, Math.max(0, (scrollProgress - 0.3 - i * 0.05) * 10))})`,
                  opacity: Math.min(1, Math.max(0, (scrollProgress - 0.3 - i * 0.05) * 10))
                }}
              />
            ))}
          </div>
          
          {/* Animated scroll indicator - black version */}
          <div 
            className="flex flex-col items-center gap-2 transition-opacity duration-300"
            style={{ opacity: scrollProgress > 0.15 && scrollProgress < 0.7 ? 1 : Math.max(0, 1 - Math.abs(scrollProgress - 0.4) * 3) }}
          >
            <span className="text-neutral-500 text-xs uppercase tracking-[0.2em] font-medium">
              Comenzá a explorar
            </span>
            
            {/* Mouse icon with scroll wheel animation - black version */}
            <div className="relative flex flex-col items-center">
              {/* Mouse outline */}
              <div className="relative w-5 h-8 rounded-full border-2 border-neutral-400 flex justify-center">
                {/* Animated scroll wheel */}
                <div className="w-1 h-1.5 bg-neutral-500 rounded-full mt-1.5 animate-scroll-wheel" />
              </div>
              
              {/* Animated line below mouse */}
              <div className="relative h-8 w-[2px] mt-1.5 overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-b from-neutral-400 via-neutral-300 to-transparent animate-scroll-line" />
                <div className="absolute top-0 left-0 w-full bg-[#0E82B5] animate-scroll-dot" style={{ height: '6px', borderRadius: '50%' }} />
              </div>
              
              {/* Triple chevron animation */}
              <div className="flex flex-col items-center -mt-0.5 gap-0">
                <ChevronDown className="w-3 h-3 text-neutral-300 animate-chevron-1" />
                <ChevronDown className="w-3 h-3 text-neutral-400 -mt-1.5 animate-chevron-2" />
                <ChevronDown className="w-3 h-3 text-neutral-500 -mt-1.5 animate-chevron-3" />
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Custom animations for this section */}
      <style>{`
        @keyframes scroll-wheel {
          0%, 100% { transform: translateY(0); opacity: 1; }
          50% { transform: translateY(3px); opacity: 0.3; }
        }
        @keyframes scroll-line {
          0% { transform: translateY(-100%); }
          100% { transform: translateY(100%); }
        }
        @keyframes scroll-dot {
          0% { transform: translateY(-6px); opacity: 0; }
          20% { opacity: 1; }
          80% { opacity: 1; }
          100% { transform: translateY(32px); opacity: 0; }
        }
        @keyframes chevron-1 {
          0%, 100% { opacity: 0.3; transform: translateY(0); }
          50% { opacity: 0.5; transform: translateY(2px); }
        }
        @keyframes chevron-2 {
          0%, 100% { opacity: 0.5; transform: translateY(0); }
          50% { opacity: 0.7; transform: translateY(2px); }
        }
        @keyframes chevron-3 {
          0%, 100% { opacity: 0.7; transform: translateY(0); }
          50% { opacity: 1; transform: translateY(2px); }
        }
        .animate-scroll-wheel {
          animation: scroll-wheel 1.5s ease-in-out infinite;
        }
        .animate-scroll-line {
          animation: scroll-line 2s ease-in-out infinite;
        }
        .animate-scroll-dot {
          animation: scroll-dot 2s ease-in-out infinite;
        }
        .animate-chevron-1 {
          animation: chevron-1 1.5s ease-in-out infinite;
        }
        .animate-chevron-2 {
          animation: chevron-2 1.5s ease-in-out infinite 0.15s;
        }
        .animate-chevron-3 {
          animation: chevron-3 1.5s ease-in-out infinite 0.3s;
        }
      `}</style>
    </section>
  );
};

// ============================================
// "IDEAL PARA" SECTION - Features showcase
// Black panel slides in first, then title, then features one by one
// ============================================

const IdealParaSection = ({ id }) => {
  const sectionRef = useRef(null);
  const [scrollProgress, setScrollProgress] = useState(0);

  const handleScroll = useCallback(() => {
    if (!sectionRef.current) return;
    const rect = sectionRef.current.getBoundingClientRect();
    const sectionHeight = sectionRef.current.offsetHeight;
    const progress = Math.max(0, Math.min(1, -rect.top / (sectionHeight - window.innerHeight)));
    setScrollProgress(progress);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const featuresGroup1 = [
    { icon: Search, label: 'Gestión de Captación', color: '#0E82B5' },
    { icon: Package, label: 'Control de Stock', color: '#F59E0B' },
    { icon: Users, label: 'Gestión de Jugadores', color: '#0E82B5' },
    { icon: ClipboardCheck, label: 'Control de Asistencias', color: '#06B6D4' },
    { icon: TrendingUp, label: 'Índice de Rendimiento', color: '#6FB035' },
    { icon: AlertTriangle, label: 'Control de Vencimientos', color: '#F59E0B' },
    { icon: History, label: 'Historial de Actividades', color: '#06B6D4' },
    { icon: UserCog, label: 'Gestión de Personal', color: '#8B5CF6' },
  ];

  const featuresGroup2 = [
    { icon: DollarSign, label: 'Gestión Financiera', color: '#6FB035' },
    { icon: Calendar, label: 'Calendario Deportivo', color: '#8B5CF6' },
    { icon: Dumbbell, label: 'Planificación de Entrenamientos', color: '#6FB035' },
    { icon: Warehouse, label: 'Inventario de Materiales', color: '#F59E0B' },
    { icon: Bell, label: 'Notificaciones', color: '#06B6D4' },
    { icon: Shield, label: 'Roles y Permisos', color: '#8B5CF6' },
    { icon: MapPin, label: 'Mapa de Captación', color: '#0E82B5' },
    { icon: Trophy, label: 'Gestión de Partidos', color: '#F59E0B' },
  ];

  // === ANIMATION PHASES (Sequential) ===
  // Phase 1: Panel slides in (0% - 8%)
  const panelSlideIn = Math.min(1, scrollProgress / 0.08);
  
  // Phase 2: Title enters AFTER panel is fully in (8% - 14%)
  const titleEnter = Math.max(0, Math.min(1, (scrollProgress - 0.08) / 0.06));
  
  // Phase 3: Group 1 features enter one by one (14% - 45%)
  // Each feature gets ~3.9% of scroll range = slower entry
  const group1Start = 0.14;
  const group1End = 0.45;
  const group1Progress = Math.max(0, Math.min(1, (scrollProgress - group1Start) / (group1End - group1Start)));
  
  // Phase 4: Transition between groups (45% - 50%)
  const transitionPhase = Math.max(0, Math.min(1, (scrollProgress - 0.45) / 0.05));
  
  // Phase 5: Group 2 features enter one by one (50% - 85%)
  const group2Start = 0.50;
  const group2End = 0.85;
  const group2Progress = Math.max(0, Math.min(1, (scrollProgress - group2Start) / (group2End - group2Start)));
  
  // Group visibility
  const showGroup1 = scrollProgress < 0.50;
  const group1Opacity = showGroup1 ? 1 : Math.max(0, 1 - transitionPhase * 2);
  const group2Opacity = scrollProgress >= 0.45 ? Math.min(1, transitionPhase * 2) : 0;

  const renderFeature = (feature, idx, groupProgress, totalFeatures) => {
    // Each feature enters one by one with clear separation
    // Feature N starts appearing when scroll reaches its "slot"
    const featureSlot = idx / totalFeatures;
    const featureSlotEnd = (idx + 0.7) / totalFeatures; // 70% of slot for full appearance
    
    // Calculate individual feature progress
    const featureProgress = Math.max(0, Math.min(1, 
      (groupProgress - featureSlot) / (featureSlotEnd - featureSlot)
    ));
    
    // Only render if this feature should be visible
    const shouldRender = groupProgress >= featureSlot * 0.8;
    
    return (
      <div
        key={idx}
        className="transition-all duration-500 ease-out"
        style={{
          opacity: shouldRender ? featureProgress : 0,
          transform: `translateX(${(1 - featureProgress) * 60}px) scale(${0.9 + featureProgress * 0.1})`,
          visibility: shouldRender ? 'visible' : 'hidden',
        }}
      >
        <div 
          className="flex items-center gap-3 px-5 py-3 rounded-lg border border-white/10 bg-white/5 hover:bg-white/10 transition-all duration-300"
        >
          <div 
            className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: `${feature.color}20` }}
          >
            <feature.icon 
              className="w-5 h-5" 
              style={{ color: feature.color }} 
            />
          </div>
          <span className="text-base font-medium text-white/90">
            {feature.label}
          </span>
        </div>
      </div>
    );
  };

  // Content only appears after panel is mostly visible
  const contentVisible = panelSlideIn > 0.7;

  return (
    <section 
      id={id}
      ref={sectionRef}
      className="relative h-[700vh] bg-white"
    >
      <div className="sticky top-0 h-screen overflow-hidden">
        {/* Black panel that slides in from the right FIRST */}
        <div 
          className="absolute inset-0 bg-[#0a0a0a] transition-transform duration-500 ease-out"
          style={{ 
            transform: `translateX(${(1 - panelSlideIn) * 100}%)`,
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-[#0a0a0a] via-[#111] to-[#0a0a0a]" />
          <div 
            className="absolute inset-0 opacity-[0.03]"
            style={{
              backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
              backgroundSize: '50px 50px'
            }}
          />
        </div>

        {/* Content - only visible after panel enters */}
        <div 
          className="relative z-10 h-full flex flex-col items-center justify-center px-4"
          style={{ 
            opacity: contentVisible ? 1 : 0,
            transition: 'opacity 0.3s ease-out'
          }}
        >
          {/* Title - enters AFTER panel */}
          <div 
            className="text-center mb-10 transition-all duration-500"
            style={{ 
              opacity: titleEnter,
              transform: `translateY(${(1 - titleEnter) * 40}px)`
            }}
          >
            <p className="font-poppins text-xs sm:text-sm uppercase tracking-[0.3em] text-[#6FB035] font-bold mb-3">
              Una plataforma
            </p>
            <h2 className="font-poppins font-black text-4xl sm:text-5xl md:text-6xl uppercase tracking-tight">
              <span className="text-white">Ideal</span>{' '}
              <span className="text-[#0E82B5]">para</span>
            </h2>
          </div>
          
          {/* Features container - stacked vertically */}
          <div className="relative w-full max-w-md mx-auto min-h-[400px]">
            {/* Group 1 */}
            <div 
              className="flex flex-col gap-3 transition-opacity duration-500"
              style={{ 
                opacity: group1Opacity,
                position: showGroup1 ? 'relative' : 'absolute',
                top: 0,
                left: 0,
                right: 0,
              }}
            >
              {featuresGroup1.map((feature, idx) => 
                renderFeature(feature, idx, group1Progress, featuresGroup1.length)
              )}
            </div>
            
            {/* Group 2 */}
            <div 
              className="flex flex-col gap-3 transition-opacity duration-500"
              style={{ 
                opacity: group2Opacity,
                position: !showGroup1 ? 'relative' : 'absolute',
                top: 0,
                left: 0,
                right: 0,
              }}
            >
              {featuresGroup2.map((feature, idx) => 
                renderFeature(feature, idx, group2Progress, featuresGroup2.length)
              )}
            </div>
          </div>
          
          {/* Progress indicator */}
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-2">
            <div 
              className="w-2 h-2 rounded-full transition-all duration-300"
              style={{ 
                backgroundColor: showGroup1 ? '#6FB035' : 'rgba(255,255,255,0.3)',
              }}
            />
            <div 
              className="w-2 h-2 rounded-full transition-all duration-300"
              style={{ 
                backgroundColor: !showGroup1 ? '#6FB035' : 'rgba(255,255,255,0.3)',
              }}
            />
          </div>
        </div>
      </div>
    </section>
  );
};

// ============================================
// MODULE SECTION COMPONENT
// Desktop: Horizontal layout (text+features left, image right)
// Mobile: Vertical layout (text, image, features)
// Features animate in on enter, animate out on exit
// ============================================

const ModuleSection = ({ 
  id,
  title, 
  subtitle,
  description,
  color, 
  bgColor,
  features,
  images,
  direction = 'right',
  nextColor
}) => {
  const sectionRef = useRef(null);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [zoomImage, setZoomImage] = useState(null);
  const [zoomIndex, setZoomIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  const handleScroll = useCallback(() => {
    if (!sectionRef.current) return;
    const rect = sectionRef.current.getBoundingClientRect();
    const sectionHeight = sectionRef.current.offsetHeight;
    const progress = Math.max(0, Math.min(1, -rect.top / (sectionHeight - window.innerHeight)));
    setScrollProgress(progress);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  // Handle browser back button to close modal
  useEffect(() => {
    if (zoomImage) {
      window.history.pushState({ modal: true }, '');
      const handlePopState = () => {
        setZoomImage(null);
      };
      window.addEventListener('popstate', handlePopState);
      return () => window.removeEventListener('popstate', handlePopState);
    }
  }, [zoomImage]);

  // Close modal on Escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (zoomImage) {
          window.history.back();
        }
      }
      if (e.key === 'ArrowLeft' && zoomImage) {
        navigateImage(-1);
      }
      if (e.key === 'ArrowRight' && zoomImage) {
        navigateImage(1);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [zoomImage, zoomIndex]);

  // Open zoom modal
  const openZoom = (image, index) => {
    if (image.src) {
      setZoomIndex(index);
      setZoomImage(image);
    }
  };

  // Close zoom modal
  const closeZoom = () => {
    window.history.back();
  };

  // Navigate between images
  const navigateImage = (direction) => {
    const newIndex = (zoomIndex + direction + images.length) % images.length;
    setZoomIndex(newIndex);
    setZoomImage(images[newIndex]);
  };

  // Touch handlers for swipe
  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    if (isLeftSwipe) {
      navigateImage(1);
    }
    if (isRightSwipe) {
      navigateImage(-1);
    }
  };

  // Animation phases
  const enterPhase = Math.min(1, scrollProgress * 4);
  const contentPhase = Math.max(0, Math.min(1, (scrollProgress - 0.1) * 2));
  const exitPhase = Math.max(0, (scrollProgress - 0.8) * 5);
  
  // Diagonal animation phase
  const diagonalPhase = Math.max(0, Math.min(1, (scrollProgress - 0.02) * 2.5));

  // Image container enters and settles first (0% - 35% scroll)
  const imageEnterPhase = Math.max(0, Math.min(1, (scrollProgress - 0.05) * 3));
  
  // Images ONLY start changing AFTER scrollProgress reaches 0.35 (35%)
  // This ensures the container is fully settled and centered before any image change
  const imageScrollStart = 0.35; // Start changing images at 35% scroll
  const imageScrollEnd = 0.72;   // End at 72%
  const imageProgress = scrollProgress >= imageScrollStart
    ? Math.max(0, Math.min(1, (scrollProgress - imageScrollStart) / (imageScrollEnd - imageScrollStart)))
    : 0; // Keep at 0 (first image) until 35% scroll
  const currentImageIndex = Math.min(
    images.length - 1,
    Math.max(0, Math.floor(imageProgress * images.length))
  );

  // Direction for animations
  const imageEnterFromRight = direction === 'right';

  // Calculate feature animations (enter and exit)
  const getFeatureAnimation = (idx) => {
    const enterStart = 0.12 + idx * 0.04;
    const enterEnd = enterStart + 0.12;
    const exitStart = 0.75 - idx * 0.03;
    const exitEnd = exitStart + 0.1;
    
    // Enter animation
    const enterProgress = Math.max(0, Math.min(1, (scrollProgress - enterStart) / (enterEnd - enterStart)));
    // Exit animation
    const exitProgress = Math.max(0, Math.min(1, (scrollProgress - exitStart) / (exitEnd - exitStart)));
    
    const opacity = enterProgress * (1 - exitProgress);
    const translateY = (1 - enterProgress) * 30 + exitProgress * -40;
    const translateX = exitProgress * (idx % 2 === 0 ? -50 : 50);
    
    return { opacity, translateY, translateX };
  };

  return (
    <section 
      id={id}
      ref={sectionRef}
      className="relative"
      style={{ height: `${350 + images.length * 50}vh` }}
    >
      <div 
        className="sticky top-0 h-screen overflow-hidden"
        style={{ backgroundColor: bgColor }}
      >
        {/* Animated diagonal geometric element */}
        <div 
          className="absolute inset-0 pointer-events-none overflow-hidden"
          style={{ opacity: enterPhase * (1 - exitPhase) }}
        >
          <div 
            className="absolute transition-all duration-1000 ease-out"
            style={{
              width: '65%',
              height: '160%',
              top: '-30%',
              right: imageEnterFromRight ? 0 : 'auto',
              left: imageEnterFromRight ? 'auto' : 0,
              background: `linear-gradient(${imageEnterFromRight ? '155deg' : '205deg'}, 
                ${color}40 0%, 
                ${color}30 50%,
                ${color}15 100%)`,
              clipPath: imageEnterFromRight 
                ? 'polygon(40% 0, 100% 0, 100% 100%, 0% 100%)'
                : 'polygon(0 0, 60% 0, 100% 100%, 0% 100%)',
              transform: `translateX(${imageEnterFromRight ? (1 - diagonalPhase) * 100 : (diagonalPhase - 1) * 100}%)`,
              opacity: diagonalPhase,
            }}
          />
        </div>

        {/* Exit diagonal overlay - geometric transition to next section */}
        <div 
          className="absolute inset-0 pointer-events-none overflow-hidden"
          style={{ 
            opacity: exitPhase,
            zIndex: 20,
          }}
        >
          <div 
            className="absolute w-[150%] h-[200%]"
            style={{
              top: '-50%',
              left: '-25%',
              backgroundColor: nextColor || '#171717',
              clipPath: 'polygon(0 60%, 100% 40%, 100% 100%, 0 100%)',
              transform: `translateY(${(1 - exitPhase) * 50}%)`,
            }}
          />
        </div>

        {/* Subtle accent bar on the side */}
        <div 
          className="absolute top-0 bottom-0 w-1 transition-all duration-700 ease-out"
          style={{ 
            backgroundColor: color,
            opacity: 0.5 * (1 - exitPhase),
            left: imageEnterFromRight ? 'auto' : 0,
            right: imageEnterFromRight ? 0 : 'auto',
            transform: `scaleY(${enterPhase})`,
            transformOrigin: 'top',
          }}
        />
        
        {/* Content container */}
        <div className="relative z-10 h-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col justify-center py-8">
          
          {/* Desktop: Horizontal layout | Mobile: Vertical */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:gap-12">
            
            {/* Left side on desktop: Module info + Features */}
            <div 
              className={`flex-1 ${imageEnterFromRight ? 'lg:order-1' : 'lg:order-2'}`}
            >
              {/* Module badge - animate in/out */}
              <div 
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg mb-4 transition-all duration-500"
                style={{ 
                  backgroundColor: `${color}15`,
                  opacity: contentPhase * (1 - exitPhase),
                  transform: `translateY(${(1 - contentPhase) * 20 + exitPhase * -30}px)`
                }}
              >
                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
                <span className="text-sm font-bold uppercase tracking-wider" style={{ color }}>{subtitle}</span>
              </div>
              
              {/* Title - animate in/out */}
              <h2 
                className="font-poppins font-black text-3xl sm:text-4xl md:text-5xl uppercase tracking-tight mb-3 transition-all duration-500"
                style={{ 
                  color,
                  opacity: enterPhase * (1 - exitPhase),
                  transform: `translateY(${(1 - enterPhase) * 30 + exitPhase * -40}px)`
                }}
              >
                {title}
              </h2>
              
              {/* Description - animate in/out */}
              <p 
                className="font-poppins text-base sm:text-lg text-neutral-600 max-w-xl transition-all duration-500 mb-6"
                style={{ 
                  opacity: contentPhase * (1 - exitPhase * 0.8),
                  transform: `translateY(${(1 - contentPhase) * 20 + exitPhase * -30}px)`
                }}
              >
                {description}
              </p>
              
              {/* Features - animate in on enter, animate out on exit (different directions) */}
              <div className="hidden lg:block space-y-3">
                {features.map((feature, idx) => {
                  const anim = getFeatureAnimation(idx);
                  return (
                    <div 
                      key={idx}
                      className="flex items-start gap-4 transition-all duration-500"
                      style={{ 
                        opacity: anim.opacity,
                        transform: `translateY(${anim.translateY}px) translateX(${anim.translateX}px)`
                      }}
                    >
                      <div 
                        className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
                        style={{ backgroundColor: `${color}15` }}
                      >
                        <feature.icon className="w-5 h-5" style={{ color }} />
                      </div>
                      <div className="flex-1 text-left">
                        <h4 className="font-semibold text-neutral-800 text-base">{feature.name}</h4>
                        <p className="text-sm text-neutral-500 mt-0.5">{feature.desc}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            
            {/* Right side on desktop: Images - centered and with entry animation */}
            <div 
              className={`flex-1 mt-6 lg:mt-0 flex flex-col items-center justify-center ${imageEnterFromRight ? 'lg:order-2' : 'lg:order-1'}`}
            >
              {/* Image label with icon - above the container */}
              {images[currentImageIndex] && (
                <div 
                  className="mb-3 flex items-center gap-2 transition-all duration-500"
                  style={{ 
                    opacity: imageEnterPhase * (1 - exitPhase),
                  }}
                >
                  <div 
                    className="w-8 h-8 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: `${color}15` }}
                  >
                    {React.createElement(images[currentImageIndex].icon, { 
                      className: "w-4 h-4", 
                      style: { color } 
                    })}
                  </div>
                  <p className="font-semibold text-base text-neutral-700">{images[currentImageIndex].label}</p>
                </div>
              )}
              
              {/* Image container - clean for screenshots */}
              <div 
                className="relative w-full max-w-md aspect-[16/10] transition-all duration-700"
                style={{ 
                  opacity: imageEnterPhase * (1 - exitPhase),
                  transform: `translateX(${imageEnterFromRight ? (1 - imageEnterPhase) * 80 : (imageEnterPhase - 1) * 80}px) scale(${0.9 + imageEnterPhase * 0.1})`
                }}
              >
                {images.map((image, idx) => {
                  const isActive = idx === currentImageIndex;
                  if (!isActive) return null;
                  
                  return (
                    <div
                      key={idx}
                      className="absolute inset-0 rounded-xl shadow-2xl overflow-hidden cursor-pointer group/image"
                      style={{
                        backgroundColor: '#f5f5f5',
                        border: `2px solid ${color}`,
                      }}
                      onClick={() => openZoom(image, idx)}
                    >
                      {image.src ? (
                        <>
                          <img 
                            src={image.src} 
                            alt={image.label}
                            className="w-full h-full object-cover object-top transition-transform duration-300 group-hover/image:scale-105"
                          />
                          {/* Zoom indicator overlay - always visible on mobile, hover on desktop */}
                          <div className="absolute inset-0 bg-black/0 group-hover/image:bg-black/20 transition-all duration-300 flex items-center justify-center">
                            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white/90 flex items-center justify-center opacity-70 sm:opacity-0 group-hover/image:opacity-100 transition-all duration-300 shadow-lg transform scale-100 sm:scale-75 group-hover/image:scale-100">
                              <ZoomIn className="w-5 h-5 sm:w-6 sm:h-6 text-neutral-700" />
                            </div>
                          </div>
                          {/* Mobile hint badge */}
                          <div className="absolute bottom-2 right-2 sm:hidden px-2 py-1 rounded bg-black/50 backdrop-blur-sm">
                            <span className="text-white text-xs">Tocar para ampliar</span>
                          </div>
                        </>
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <span className="text-neutral-300 text-sm">Captura de pantalla</span>
                        </div>
                      )}
                    </div>
                  );
                })}
                
                {/* Image indicators */}
                <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 flex gap-2">
                  {images.map((_, idx) => (
                    <div
                      key={idx}
                      className="h-1.5 rounded-full transition-all duration-300"
                      style={{ 
                        width: idx === currentImageIndex ? '20px' : '6px',
                        backgroundColor: idx === currentImageIndex ? color : `${color}40`
                      }}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
          
          {/* Features for mobile - animate in/out */}
          <div className="lg:hidden w-full max-w-md mx-auto mt-12">
            <div className="space-y-3">
              {features.map((feature, idx) => {
                const anim = getFeatureAnimation(idx);
                return (
                  <div 
                    key={idx}
                    className="flex items-start gap-4 transition-all duration-500"
                    style={{ 
                      opacity: anim.opacity,
                      transform: `translateY(${anim.translateY}px) translateX(${anim.translateX}px)`
                    }}
                  >
                    <div 
                      className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
                      style={{ backgroundColor: `${color}15` }}
                    >
                      <feature.icon className="w-5 h-5" style={{ color }} />
                    </div>
                    <div className="flex-1 text-left">
                      <h4 className="font-semibold text-neutral-800 text-base">{feature.name}</h4>
                      <p className="text-sm text-neutral-500 mt-0.5">{feature.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        
        {/* Transition gradient to next section */}
        {nextColor && (
          <div 
            className="absolute bottom-0 left-0 right-0 h-32 transition-opacity duration-500"
            style={{ 
              background: `linear-gradient(to bottom, transparent, ${nextColor})`,
              opacity: exitPhase
            }}
          />
        )}
      </div>

      {/* Zoom Modal with Navigation */}
      {zoomImage && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 backdrop-blur-sm animate-fadeIn"
          onClick={closeZoom}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          {/* Close button */}
          <button 
            className="absolute top-4 right-4 z-10 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors duration-200"
            onClick={closeZoom}
          >
            <X className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
          </button>
          
          {/* Image label and counter */}
          <div className="absolute top-4 left-4 z-10 px-3 py-2 rounded-lg bg-white/10 backdrop-blur-sm">
            <p className="text-white font-medium text-sm sm:text-base">{zoomImage.label}</p>
            <p className="text-white/60 text-xs">{zoomIndex + 1} / {images.length}</p>
          </div>
          
          {/* Navigation arrows - Desktop */}
          {images.length > 1 && (
            <>
              <button
                className="hidden sm:flex absolute left-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 items-center justify-center transition-all duration-200 hover:scale-110"
                onClick={(e) => { e.stopPropagation(); navigateImage(-1); }}
              >
                <ChevronDown className="w-6 h-6 text-white rotate-90" />
              </button>
              <button
                className="hidden sm:flex absolute right-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 items-center justify-center transition-all duration-200 hover:scale-110"
                onClick={(e) => { e.stopPropagation(); navigateImage(1); }}
              >
                <ChevronDown className="w-6 h-6 text-white -rotate-90" />
              </button>
            </>
          )}
          
          {/* Zoomed image */}
          <img 
            src={zoomImage.src} 
            alt={zoomImage.label}
            className="max-w-[95vw] sm:max-w-[85vw] max-h-[80vh] object-contain rounded-lg shadow-2xl animate-zoomIn"
            onClick={(e) => e.stopPropagation()}
          />
          
          {/* Image indicators/thumbnails */}
          {images.length > 1 && (
            <div className="absolute bottom-16 sm:bottom-8 left-1/2 -translate-x-1/2 flex gap-2 px-4 py-2 rounded-full bg-black/50 backdrop-blur-sm">
              {images.map((img, idx) => (
                <button
                  key={idx}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    idx === zoomIndex 
                      ? 'w-6 bg-white' 
                      : 'w-2 bg-white/40 hover:bg-white/60'
                  }`}
                  onClick={(e) => { 
                    e.stopPropagation(); 
                    setZoomIndex(idx);
                    setZoomImage(images[idx]);
                  }}
                />
              ))}
            </div>
          )}
          
          {/* Hint text */}
          <p className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/40 text-xs sm:text-sm text-center px-4">
            <span className="hidden sm:inline">← → para navegar · ESC para cerrar</span>
            <span className="sm:hidden">Deslizá para navegar · Atrás para cerrar</span>
          </p>
        </div>
      )}

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes zoomIn {
          from { opacity: 0; transform: scale(0.9); }
          to { opacity: 1; transform: scale(1); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out forwards;
        }
        .animate-zoomIn {
          animation: zoomIn 0.3s ease-out forwards;
        }
      `}</style>
    </section>
  );
};

// ============================================
// CTA SECTION
// ============================================

const CTASection = ({ id }) => {
  const sectionRef = useRef(null);
  const [scrollProgress, setScrollProgress] = useState(0);

  const handleScroll = useCallback(() => {
    if (!sectionRef.current) return;
    const rect = sectionRef.current.getBoundingClientRect();
    const sectionHeight = sectionRef.current.offsetHeight;
    const progress = Math.max(0, Math.min(1, -rect.top / (sectionHeight - window.innerHeight)));
    setScrollProgress(progress);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const enterPhase = Math.min(1, scrollProgress * 2);
  const moreFeatures = [
    { 
      icon: BarChart3, 
      title: 'Centro de reportes', 
      desc: 'Posibilidad de generar informes para todos los módulos de la plataforma.',
      color: '#0E82B5'
    },
    { 
      icon: Cpu, 
      title: 'Integración de IA', 
      desc: 'Posibilidades de agentes de consultas, análisis y mucho más.',
      color: '#8B5CF6'
    },
    { 
      icon: Wifi, 
      title: 'Conexión con GPS wearables', 
      desc: 'Análisis de rendimiento en tiempo real para que se agreguen al sistema.',
      color: '#6FB035'
    },
    { 
      icon: Plug, 
      title: 'Integración', 
      desc: 'Posibilidad de integración con otras plataformas especializadas en otros rubros.',
      color: '#F59E0B'
    },
  ];

  return (
    <section 
      id={id}
      ref={sectionRef}
      className="relative h-[350vh]"
    >
      {/* Y MUCHO MÁS Section - with clean dark background */}
      <div className="sticky top-0 h-screen overflow-hidden bg-neutral-900">
        {/* Subtle geometric background - no cross pattern */}
        <div className="absolute inset-0">
          <div 
            className="absolute w-full h-full"
            style={{
              background: 'linear-gradient(180deg, #171717 0%, #0f0f0f 100%)',
            }}
          />
          {/* Subtle corner accent */}
          <div 
            className="absolute bottom-0 right-0 w-1/3 h-1/3"
            style={{
              background: 'linear-gradient(135deg, transparent 0%, #6FB03508 100%)',
            }}
          />
          <div 
            className="absolute top-0 left-0 w-1/3 h-1/3"
            style={{
              background: 'linear-gradient(315deg, transparent 0%, #0E82B508 100%)',
            }}
          />
        </div>

        <div className="relative z-10 h-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col justify-center">
          {/* Title */}
          <div 
            className="text-center mb-12 transition-all duration-700"
            style={{ 
              opacity: enterPhase,
              transform: `translateY(${(1 - enterPhase) * 40}px)`
            }}
          >
            <h2 className="font-poppins font-black text-3xl sm:text-4xl md:text-5xl text-white uppercase tracking-tight">
              Y <span className="text-[#6FB035]">mucho más</span>...
            </h2>
          </div>

          {/* Features grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            {moreFeatures.map((feature, idx) => {
              const delay = idx * 0.08;
              const featureProgress = Math.max(0, Math.min(1, (enterPhase - delay) * 1.5));
              
              return (
                <div 
                  key={idx}
                  className="group relative p-6 rounded-lg border border-white/10 bg-white/5 backdrop-blur-sm transition-all duration-500 hover:bg-white/10 hover:border-white/20"
                  style={{
                    opacity: featureProgress,
                    transform: `translateY(${(1 - featureProgress) * 30}px) translateX(${idx % 2 === 0 ? (1 - featureProgress) * -30 : (1 - featureProgress) * 30}px)`,
                  }}
                >
                  {/* Diagonal accent on hover */}
                  <div 
                    className="absolute top-0 right-0 w-16 h-16 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                    style={{
                      background: `linear-gradient(135deg, transparent 50%, ${feature.color}20 50%)`,
                    }}
                  />
                  
                  <div className="flex items-start gap-4">
                    <div 
                      className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 transition-transform duration-300 group-hover:scale-110"
                      style={{ backgroundColor: `${feature.color}20` }}
                    >
                      <feature.icon className="w-6 h-6" style={{ color: feature.color }} />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-poppins font-bold text-lg text-white mb-2">{feature.title}</h3>
                      <p className="text-neutral-400 text-sm leading-relaxed">{feature.desc}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Scroll indicator to CTA */}
        <div 
          className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 transition-opacity duration-300"
          style={{ opacity: Math.min(1, enterPhase * 2) * (1 - Math.max(0, (scrollProgress - 0.55) * 3)) }}
        >
          <ChevronDown className="w-6 h-6 text-white/40 animate-bounce" />
        </div>
      </div>

      {/* EMPEZÁ HOY Section */}
      <div 
        className="sticky top-0 h-screen flex flex-col items-center justify-center px-4 bg-neutral-900"
        style={{
          opacity: Math.max(0, (scrollProgress - 0.6) * 2.5),
        }}
      >
        {/* Geometric accents */}
        <div className="absolute inset-0 overflow-hidden">
          <div 
            className="absolute top-0 left-0 w-1/3 h-full"
            style={{
              background: 'linear-gradient(to right, #6FB03510, transparent)',
              clipPath: 'polygon(0 0, 100% 20%, 80% 100%, 0 100%)',
            }}
          />
          <div 
            className="absolute top-0 right-0 w-1/3 h-full"
            style={{
              background: 'linear-gradient(to left, #0E82B510, transparent)',
              clipPath: 'polygon(20% 0, 100% 0, 100% 100%, 0 80%)',
            }}
          />
        </div>
        
        <div className="relative z-10 text-center">
          <h2 className="font-poppins font-black text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-white uppercase tracking-tight mb-4">
            <span className="text-[#6FB035]">Empezá</span> hoy
          </h2>
          
          <p className="font-poppins text-lg sm:text-xl text-neutral-400 max-w-2xl mx-auto mb-10">
            Transformá y centralizá la gestión de tu club con la plataforma más versátil.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="https://wa.me/59897515555?text=Hola%2C%20quiero%20solicitar%20acceso%20a%20la%20demo%20de%20SportCore"
              target="_blank"
              rel="noopener noreferrer"
              className="group relative bg-[#6FB035] text-white font-black py-4 px-12 uppercase tracking-wider overflow-hidden transition-all hover:shadow-[0_0_40px_rgba(111,176,53,0.4)] flex items-center justify-center gap-3"
            >
              <MessageCircle className="w-5 h-5" />
              <span className="relative z-10">Solicitá acceso a la demo</span>
              <div className="absolute inset-0 bg-white/20 translate-x-[-100%] group-hover:translate-x-0 transition-transform duration-300" />
            </a>
            
            <a 
              href="mailto:contacto@sportcontrol.com"
              className="border-2 border-white/30 text-white font-black py-4 px-12 uppercase tracking-wider transition-all hover:border-[#0E82B5] hover:text-[#0E82B5]"
            >
              Contactanos
            </a>
          </div>
        </div>
        
        {/* Footer */}
        <div className="absolute bottom-8 text-center">
          <p className="text-neutral-500 text-sm">
            © 2026 SportCore. Todos los derechos reservados.
          </p>
        </div>
      </div>
    </section>
  );
};

// ============================================
// MAIN ABOUT PAGE
// ============================================

const About = () => {
  // Module data - Updated with more images
  const modules = [
    {
      id: 'plantel',
      title: 'Gestión de jugadores',
      subtitle: 'Módulo 1',
      description: 'Desde formativas hasta primera. Centraliza la gestión integral de todos los jugadores para la mejor toma de decisiones.',
      color: '#0E82B5',
      bgColor: '#F8FAFC',
      direction: 'right',
      features: [
        { name: 'Fichas de Jugadores', desc: 'Datos completos, fotos, documentos', icon: Users },
        { name: 'Categorías', desc: 'Organización por divisiones', icon: Shirt },
        { name: 'Mapa de Jugadores', desc: 'Ubicación geográfica y zonas', icon: MapPin },
        { name: 'Captación (Scouts)', desc: 'Evaluación de nuevos talentos', icon: Search },
      ],
      images: [
        { label: 'Listado de Jugadores', icon: Users, src: 'https://customer-assets.emergentagent.com/job_sportcore-features/artifacts/um8psv9e_Listado_Jugadores.jpg' },
        { label: 'Ficha del Jugador', icon: Shirt, src: 'https://customer-assets.emergentagent.com/job_sportcore-features/artifacts/gf349w2r_Ficha-Jugador.jpg' },
        { label: 'Índice rendimiento', icon: TrendingUp, src: 'https://customer-assets.emergentagent.com/job_sportcore-features/artifacts/mi8k8ich_Indice%20rendimiento.jpg' },
        { label: 'Gráfica de progresión', icon: BarChart3, src: 'https://customer-assets.emergentagent.com/job_sportcore-features/artifacts/1p0ujfkd_grafica_Progresion.jpg' },
        { label: 'Mapa de Ubicaciones', icon: MapPin, src: 'https://customer-assets.emergentagent.com/job_sportcore-features/artifacts/83h5z3dm_mapa.jpg' },
      ],
    },
    {
      id: 'actividad',
      title: 'Actividades',
      subtitle: 'Módulo 2',
      description: 'Centraliza y gestiona todas las actividades del club en un solo lugar, con agilidad, orden y control.',
      color: '#6FB035',
      bgColor: '#F7FDF4',
      direction: 'left',
      features: [
        { name: 'Calendario Integral', desc: 'Vista semanal de todo el club', icon: Calendar },
        { name: 'Entrenamientos', desc: 'Por categoría, tipo y locación', icon: Dumbbell },
        { name: 'Partidos', desc: 'Rivales, convocatorias, resultados', icon: Trophy },
        { name: 'Asistencias', desc: 'Control de presentes por evento', icon: ClipboardCheck },
      ],
      images: [
        { label: 'Calendario Semanal', icon: Calendar, src: 'https://customer-assets.emergentagent.com/job_sportcore-features/artifacts/kjimsi3d_Calendario_Semanal.jpg' },
        { label: 'Detalle de Entrenamiento', icon: Dumbbell, src: 'https://customer-assets.emergentagent.com/job_sportcore-features/artifacts/jivsohoj_DetalleEntrneamiento.jpg' },
        { label: 'Lista de entrenamiento', icon: ClipboardCheck, src: 'https://customer-assets.emergentagent.com/job_sportcore-features/artifacts/72mjntct_Lista_entrenamiento.jpg' },
        { label: 'Registro de Asistencia', icon: ClipboardCheck, src: 'https://customer-assets.emergentagent.com/job_sportcore-features/artifacts/5wq2de7v_registro_de_sistema.jpg' },
      ],
    },
    {
      id: 'recursos',
      title: 'Recursos',
      subtitle: 'Módulo 3',
      description: 'Controlá todo el material del club y sus finanzas en un solo lugar, con trazabilidad completa y control real.',
      color: '#F59E0B',
      bgColor: '#FFFBEB',
      direction: 'right',
      features: [
        { name: 'Control de Stock', desc: 'Inventario completo de materiales', icon: Boxes },
        { name: 'Movimientos de Stock', desc: 'Entradas, salidas y ajustes', icon: TrendingUp },
        { name: 'Panel Financiero', desc: 'Ingresos, egresos y balance', icon: DollarSign },
        { name: 'Alertas de Stock Bajo', desc: 'Notificaciones automáticas', icon: AlertTriangle },
        { name: 'Locaciones', desc: 'Canchas y espacios del club', icon: MapPin },
      ],
      images: [
        { label: 'Inventario de Materiales', icon: Boxes, src: 'https://customer-assets.emergentagent.com/job_sportcore-features/artifacts/7vquwk4g_Inventario_materiales.jpg' },
        { label: 'Movimientos de Stock', icon: TrendingUp, src: 'https://customer-assets.emergentagent.com/job_sportcore-features/artifacts/eknsglox_SportCore_0033_Capa%2016.jpg' },
        { label: 'Panel de Finanzas', icon: DollarSign, src: 'https://customer-assets.emergentagent.com/job_sportcore-features/artifacts/49bh2aw3_Finanzas.jpg' },
        { label: 'Alertas', icon: AlertTriangle, src: 'https://customer-assets.emergentagent.com/job_sportcore-features/artifacts/l494hg5l_Alertas.jpg' },
      ],
    },
    {
      id: 'equipo',
      title: 'Staff',
      subtitle: 'Módulo 4',
      description: 'Cada persona ve exactamente lo que necesita. 6 roles con permisos específicos para cada función.',
      color: '#8B5CF6',
      bgColor: '#FAF5FF',
      direction: 'left',
      features: [
        { name: 'Staff Técnico', desc: 'Entrenadores y preparadores', icon: UserCog },
        { name: 'Sistema de 6 Roles', desc: 'SuperAdmin, Gerencia, Admin, Coach, Intendente, Scout', icon: Shield },
        { name: 'Permisos Granulares', desc: 'Cada rol ve lo que necesita', icon: Eye },
        { name: 'Historial de Acciones', desc: 'Auditoría completa del sistema', icon: History },
      ],
      images: [
        { label: 'Equipo de Trabajo', icon: UserCog, src: 'https://customer-assets.emergentagent.com/job_sportcore-features/artifacts/yb059qyj_Equipo_de_trabajo.jpg' },
        { label: 'Gestión de Usuarios', icon: Users, src: 'https://customer-assets.emergentagent.com/job_sportcore-features/artifacts/qbs2wf2x_Gestion_Usuarios.jpg' },
        { label: 'Permisos por Rol', icon: Lock, src: 'https://customer-assets.emergentagent.com/job_sportcore-features/artifacts/vpuz1j9a_Permiso_por_Rol.jpg' },
        { label: 'Historial', icon: History, src: 'https://customer-assets.emergentagent.com/job_sportcore-features/artifacts/17u02vh6_Historial.jpg' },
      ],
    },
    {
      id: 'comunicacion',
      title: 'Comunicación',
      subtitle: 'Módulo 5 • Próximamente',
      description: 'Llegá a todos con un click. Notificaciones, citaciones y comunicados a jugadores, padres y staff.',
      color: '#06B6D4',
      bgColor: '#ECFEFF',
      direction: 'right',
      features: [
        { name: 'Centro de Comunicaciones', desc: 'Envío de comunicados masivos', icon: MessageSquare },
        { name: 'Listas de Contactos', desc: 'Padres, jugadores, staff', icon: Baby },
        { name: 'Citaciones', desc: 'Convocatorias a entrenamientos y partidos', icon: Bell },
        { name: 'Múltiples Canales', desc: 'WhatsApp, Email y Push', icon: Target },
      ],
      images: [
        { label: 'Centro de Comunicaciones', icon: MessageSquare, src: 'https://customer-assets.emergentagent.com/job_sportcore-features/artifacts/c6sql75g_Centro_de_comunicaciones.jpg' },
        { label: 'Listas de Contactos', icon: Users, src: 'https://customer-assets.emergentagent.com/job_sportcore-features/artifacts/xwehc7o5_listas.jpg' },
        { label: 'Tipos de Comunicado', icon: FileText, src: 'https://customer-assets.emergentagent.com/job_sportcore-features/artifacts/c6sql75g_Centro_de_comunicaciones.jpg' },
        { label: 'Envío de Citaciones', icon: Bell, src: 'https://customer-assets.emergentagent.com/job_sportcore-features/artifacts/xwehc7o5_listas.jpg' },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-black">
      <HeroSection />
      <IntroSection />
      <IdealParaSection id="ideal-para" />
      
      {modules.map((module, idx) => (
        <ModuleSection
          key={module.id}
          id={idx === 0 ? 'modulo-plantel' : `modulo-${module.id}`}
          {...module}
          nextColor={modules[idx + 1]?.bgColor || '#171717'}
        />
      ))}
      
      <CTASection id="cta" />
    </div>
  );
};

export default About;
